import { StorageHandler } from "@/lib/storage";
import { makeAutoObservable, runInAction } from "mobx";

export interface DebuggerState {
  isEnabled: boolean;
  isMinimized: boolean;
  isDevModeEnabled: boolean;
  isAdvancedDetailsEnabled: boolean;
  isTourDetailsOpenedFromGuideList: boolean;
  isGuideListModalVisible: boolean;
  isTourDetailsModalVisible: boolean;
  isUserDataModalVisible: boolean;
  isReportBugModalVisible: boolean;
  isStateDataModalVisible: boolean;
  selectedGuideId?: string;
  selectedStepId?: string;
  advancedDetailsTab: string;
  capturedEvents?: any[];
}

export class DebuggerStateStorage {
  private storage: StorageHandler;
  private _state: DebuggerState;

  constructor(storage: StorageHandler) {
    this.storage = storage;
    this._state = this.get();
    makeAutoObservable(this);
  }

  private storageKey(): string {
    return "pyne-debugger-state";
  }

  get isEnabled(): boolean {
    return this._state.isEnabled;
  }

  get isMinimized(): boolean {
    return this._state.isMinimized;
  }

  get isAdvancedDetailsEnabled(): boolean {
    return this._state.isAdvancedDetailsEnabled;
  }

  get isTourDetailsOpenedFromGuideList(): boolean {
    return this._state.isTourDetailsOpenedFromGuideList;
  }

  get isGuideListModalVisible(): boolean {
    return this._state.isGuideListModalVisible;
  }

  get isTourDetailsModalVisible(): boolean {
    return this._state.isTourDetailsModalVisible;
  }

  get isUserDataModalVisible(): boolean {
    return this._state.isUserDataModalVisible;
  }

  get isReportBugModalVisible(): boolean {
    return this._state.isReportBugModalVisible;
  }

  get selectedGuideId(): string | undefined {
    return this._state.selectedGuideId;
  }

  get selectedStepId(): string | undefined {
    return this._state.selectedStepId;
  }

  get advancedDetailsTab(): string {
    return this._state.advancedDetailsTab;
  }

  get capturedEventsLength() {
    return this._state.capturedEvents?.length;
  }

  public get(): DebuggerState {
    return this.storage.get(this.storageKey(), {
      isEnabled: false,
      isMinimized: true,
      isAdvancedDetailsEnabled: false,
      isTourDetailsOpenedFromGuideList: false,
      isGuideListModalVisible: false,
      isTourDetailsModalVisible: false,
      isUserDataModalVisible: false,
      isReportBugModalVisible: false,
      isStateDataModalVisible: false,
      advancedDetailsTab: "targeting",
      capturedEvents: [],
    });
  }

  public clear(): void {
    this.storage.remove(this.storageKey());
    this._state = this.get();
  }

  public set(state: Partial<DebuggerState>): void {
    runInAction(() => {
      this._state = { ...this._state, ...state };
      this.storage.set(this.storageKey(), this._state);
    });
  }

  public setCapturedEvents(events: any[]): void {
    this.set({ capturedEvents: events.slice(-500) });
  }

  public clearCapturedEvents(): void {
    this.set({ capturedEvents: [] });
  }

  public enable(): void {
    this.set({ isEnabled: true });
  }

  public disable(): void {
    this.set({ isEnabled: false });
  }
}
